import {
  AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { ThemeService } from '../../services/theme.service';
import { chartColors, chartPalette } from './chart-theme';
import { EvertimePlacementRevenueRow } from '../../services/apis/dashboard/dashboard.service';

export interface PlacementRevenueChartEvent {
  dataIndex: number;
  nativeEvent: MouseEvent;
}

/**
 * Placement Revenue by Month — ECharts stacked bar chart
 * Displays revenue broken down by placement type (Permanent, Contract, Temporary)
 */
@Component({
  selector: 'app-placement-revenue-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  host: { class: 'block w-full h-full' },
  templateUrl: './placement-revenue-chart.component.html',
})
export class PlacementRevenueChartComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {
  @Input() data: EvertimePlacementRevenueRow[] = [];
  @Output() barClick = new EventEmitter<PlacementRevenueChartEvent>();
  @Output() barContextMenu = new EventEmitter<PlacementRevenueChartEvent>();

  options: EChartsOption | null = null;
  readonly viewportSize = 12;
  viewportStart = 0;

  @ViewChild('chartContainer', { static: true }) private chartContainer?: ElementRef<HTMLElement>;

  private hasBuiltOptions = false;
  private themeSub?: Subscription;
  private chartInstance?: { resize: () => void };
  private resizeObserver?: ResizeObserver;
  private dataReference: EvertimePlacementRevenueRow[] | null = null;
  private lastWheelTime = 0;
  private readonly themeService = inject(ThemeService);

  ngOnInit(): void {
    if (!this.hasBuiltOptions) {
      this.buildOptions();
    }
    this.themeSub = this.themeService.isDark$.pipe(skip(1)).subscribe(() => this.buildOptions());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data !== this.dataReference) {
      this.dataReference = this.data;
      this.viewportStart = 0;
    }
    this.buildOptions();
  }

  ngAfterViewInit(): void {
    if (!this.chartContainer?.nativeElement) return;
    this.chartContainer.nativeElement.addEventListener('wheel', this.onNativeWheel, { passive: false, capture: true });
    this.resizeObserver = new ResizeObserver(() => this.chartInstance?.resize());
    this.resizeObserver.observe(this.chartContainer.nativeElement);
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    this.chartContainer?.nativeElement.removeEventListener('wheel', this.onNativeWheel, true);
  }

  onChartClick(params: any): void {
    if (typeof params?.dataIndex === 'number') {
      this.barClick.emit({
        dataIndex: params.dataIndex + this.viewportStart,
        nativeEvent: params.event?.event ?? params.event
      });
    }
  }

  onChartContextMenu(params: any): void {
    if (typeof params?.dataIndex !== 'number') return;
    const nativeEvent = params.event?.event ?? params.event;
    nativeEvent?.preventDefault?.();
    this.barContextMenu.emit({
      dataIndex: params.dataIndex + this.viewportStart,
      nativeEvent
    });
  }

  onChartInit(instance: { resize: () => void }): void {
    this.chartInstance = instance;
    this.chartInstance.resize();
  }

  get hasScrollableViewport(): boolean { return this.allMonthKeys().length > this.viewportSize; }
  get canMoveOlder(): boolean { return this.viewportStart > 0; }
  get canMoveNewer(): boolean { return this.viewportStart < Math.max(0, this.allMonthKeys().length - this.viewportSize); }
  get viewportLabel(): string {
    const keys = this.visibleMonthKeys();
    if (!keys.length) return '';
    return `${this.monthLabel(keys[0])} – ${this.monthLabel(keys[keys.length - 1])}`;
  }
  moveViewport(direction: 'older' | 'newer'): void {
    const max = Math.max(0, this.allMonthKeys().length - this.viewportSize);
    const next = Math.max(0, Math.min(max, this.viewportStart + (direction === 'newer' ? 1 : -1)));
    if (next === this.viewportStart) return;
    this.viewportStart = next;
    this.buildOptions();
  }
  private readonly onNativeWheel = (event: WheelEvent): void => {
    if (!this.hasScrollableViewport) return;
    event.preventDefault(); event.stopPropagation();
    const now = Date.now(); if (now - this.lastWheelTime < 120) return; this.lastWheelTime = now;
    this.moveViewport(event.deltaY > 0 ? 'newer' : 'older');
  };

  private buildOptions(): void {
    this.hasBuiltOptions = true;
    const visibleKeys = new Set(this.visibleMonthKeys());
    const rows = this.hasScrollableViewport ? (this.data ?? []).filter(row => visibleKeys.has(row.month)) : (this.data ?? []);

    if (rows.length === 0) {
      this.options = null;
      return;
    }

    const c = chartColors();

    const monthsMap = new Map<string, Map<string, number>>();
    const monthLabelsMap = new Map<string, string>();
    const categoryOrder = Array.from(new Set(rows.map(row => row.category).filter(Boolean)));
    const categoryTotals = new Map(categoryOrder.map(category => [category, 0]));

    rows.forEach(row => {
      if (!monthsMap.has(row.month)) {
        monthsMap.set(row.month, new Map());
        monthLabelsMap.set(row.month, row.monthLabel);
      }

      const categoryMap = monthsMap.get(row.month)!;
      categoryMap.set(row.category, (categoryMap.get(row.category) || 0) + row.revenue);
      categoryTotals.set(row.category, (categoryTotals.get(row.category) ?? 0) + row.revenue);
    });

    const legendOrder = [...categoryOrder].sort((a, b) =>
      (categoryTotals.get(a) ?? 0) - (categoryTotals.get(b) ?? 0)
    );

    // Build series data in category order
    const monthKeys = Array.from(monthsMap.keys()).sort((a, b) => {
      // Handle both YYYY-MM and MonthName YYYY formats
      const dateA = new Date(a.startsWith('20') ? a + '-01' : a);
      const dateB = new Date(b.startsWith('20') ? b + '-01' : b);
      return dateA.getTime() - dateB.getTime();
    });

    // Get display labels for X-axis
    const monthLabels = monthKeys.map(k => monthLabelsMap.get(k) || k);
    const largestMonthlyTotal = Math.max(...monthKeys.map(month =>
      Array.from(monthsMap.get(month)?.values() ?? []).reduce((sum, value) => sum + value, 0)
    ));
    const minimumVisibleValue = largestMonthlyTotal * 0.015;

    const seriesData: { name: string; data: Array<{ value: number; actualValue: number } | null> }[] = [];

    // Each month gets its own low-to-high series sequence. A category can have a
    // different stack position each month while retaining its permanent color.
    monthKeys.forEach((month, monthIndex) => {
      const monthlyValues = Array.from(monthsMap.get(month)?.entries() ?? [])
        .filter(([, value]) => value !== 0)
        .sort(([, a], [, b]) => a - b);

      monthlyValues.forEach(([category, value]) => {
        seriesData.push({
          name: category,
          data: monthKeys.map((_, index) => index === monthIndex ? {
            value: Math.max(value, minimumVisibleValue),
            actualValue: value,
          } : null)
        });
      });
    });

    const palette = chartPalette();
    const categoryColors = Object.fromEntries(
      categoryOrder.map((category, index) => [
        category,
        palette[index % palette.length] || c.primary,
      ])
    );

    // Format currency for tooltip
    const formatCurrency = (value: number) => {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    };

    const escapeHtml = (value: unknown) => String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

    this.options = {
      grid: { left: 34, right: 12, top: 14, bottom: 50, containLabel: true },
      tooltip: {
        trigger: 'axis',
        confine: true,
        axisPointer: {
          type: 'none'
        },
        backgroundColor: '#ffffff',
        borderColor: '#e2e8f0',
        padding: [12, 16],
        textStyle: { color: '#1e293b', fontSize: 12 },
        extraCssText: 'box-sizing: border-box; max-width: calc(100% - 16px); box-shadow: 0 4px 20px rgba(0,0,0,0.1); border-radius: 8px; border: 1px solid #e2e8f0;',
        position: (point, _params, _element, _rect, size) => {
          const margin = 8;
          const gap = 12;
          const contentWidth = Math.min(size.contentSize[0], size.viewSize[0] - margin * 2);
          const contentHeight = Math.min(size.contentSize[1], size.viewSize[1] - margin * 2);

          let left = point[0] + gap;
          if (left + contentWidth > size.viewSize[0] - margin) {
            left = point[0] - contentWidth - gap;
          }

          return [
            Math.max(margin, Math.min(left, size.viewSize[0] - contentWidth - margin)),
            Math.max(margin, Math.min(
              point[1] - contentHeight / 2,
              size.viewSize[1] - contentHeight - margin
            )),
          ];
        },
        formatter: (params: any) => {
          if (!Array.isArray(params) || params.length === 0) return '';

          const monthLabel = escapeHtml(params[0].axisValue);
          const actualValue = (param: any): number =>
            typeof param?.data?.actualValue === 'number'
              ? param.data.actualValue
              : (typeof param?.value === 'number' ? param.value : 0);
          const total = params.reduce((sum: number, param: any) => sum + actualValue(param), 0);

          let tooltipHtml = `<div class="select-none" style="font-family: Inter, sans-serif;">
      <p class="text-xs text-slate-500 mb-2 font-semibold" style="margin: 0 0 8px 0; color: #64748b;">${monthLabel}</p>`;

          categoryOrder.forEach(category => {
            const value = params
              .filter((param: any) => param.seriesName === category)
              .reduce((sum: number, param: any) => sum + actualValue(param), 0);
            if (value === 0) return;

            const dotColor = categoryColors[category];
            const categoryLabel = escapeHtml(category);

            tooltipHtml += `<div class="flex items-center justify-between gap-4 mb-1" style="display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 4px;">
        <div class="flex items-center gap-2" style="display: flex; align-items: center; gap: 8px;">
          <span style="display: inline-block; width: 10px; height: 10px; border-radius: 2px; background-color: ${dotColor};"></span>
          <span class="text-sm font-medium" style="color: #475569; font-size: 13px;">${categoryLabel}:</span>
        </div>
        <span class="text-sm font-bold" style="color: #0f172a; font-size: 13px; font-weight: 700;">${formatCurrency(value)}</span>
      </div>`;
          });

          tooltipHtml += `<div style="border-top: 1px solid #e2e8f0; margin-top: 8px; padding-top: 8px; display: flex; align-items: center; justify-content: space-between; gap: 16px;">
      <span class="text-sm font-semibold" style="color: #1e293b; font-size: 13px;">Total:</span>
      <span class="text-sm font-bold" style="color: #0f172a; font-size: 13px; font-weight: 700;">${formatCurrency(total)}</span>
    </div>`;

          tooltipHtml += `</div>`;
          return tooltipHtml;
        }
      },
      legend: {
        data: legendOrder.map(cat => ({
          name: cat,
          icon: 'rect',
          itemStyle: { color: categoryColors[cat] }
        })),
        textStyle: { color: c.textSecondary, fontSize: 11, fontWeight: 500 },
        bottom: 0,
      },
      xAxis: {
        type: 'category',
        data: monthLabels,
        axisLabel: {
          color: c.textSecondary,
          fontSize: 11,
          fontWeight: 500,
          interval: 0,
          rotate: 35,
        },
        axisLine: { lineStyle: { color: c.cardBorder } },
        splitLine: { show: false },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          color: c.textSecondary,
          fontSize: 11,
          fontWeight: 500,
          formatter: (val: number) => {
            if (val >= 1000000) return (val / 1000000).toFixed(0) + 'M';
            if (val >= 1000) return (val / 1000).toFixed(0) + 'K';
            return val.toFixed(0);
          }
        },
        axisLine: { lineStyle: { color: c.cardBorder } },
        splitLine: { lineStyle: { color: c.cardBorder, type: 'dashed' } }
      },
      series: seriesData.map(s => ({
        name: s.name,
        type: 'bar',
        stack: 'total',
        barMaxWidth: 56,
        data: s.data,
        itemStyle: {
          color: categoryColors[s.name],
          opacity: 1
        },
        emphasis: {
          focus: 'none',
          itemStyle: {
            color: categoryColors[s.name],
            opacity: 1
          }
        },
        blur: {
          itemStyle: {
            color: categoryColors[s.name],
            opacity: 1
          }
        },
        select: {
          itemStyle: {
            color: categoryColors[s.name],
            opacity: 1
          }
        }
      }))
    };
  }

  private allMonthKeys(): string[] {
    return Array.from(new Set((this.data ?? []).map(row => row.month).filter(Boolean))).sort((a, b) =>
      new Date(a.startsWith('20') ? `${a}-01` : a).getTime() - new Date(b.startsWith('20') ? `${b}-01` : b).getTime()
    );
  }
  private visibleMonthKeys(): string[] { return this.allMonthKeys().slice(this.viewportStart, this.viewportStart + this.viewportSize); }
  private monthLabel(key: string): string { return (this.data ?? []).find(row => row.month === key)?.monthLabel || key; }
}
